<template>
  <StickyFooter :sticky="canContinue">
    <template #header>
      <section class="hero is-info">
        <div class="loader-hero-body">
          <span class="loader-hero-title">Verify registration information</span>
          <ul class="loader-step-checklist">
            <li
              v-if="assignSamples"
              class="mr-2"
              :class="{ 'step-done': sampleType }"
            >
              Select sample type
            </li>
            <li class="mr-2" :class="{ 'step-done': !missStudies }">
              Assign a study to each collection
            </li>
          </ul>
        </div>
      </section>
    </template>
    <template #body>
      <div class="padded-container verify-body">
        <div v-if="assayId" class="verify-section">
          <label class="verify-section-label">Assay</label>
          <a
            class="tag is-primary is-light is-medium assay-toggle"
            @click="showAssayDetails = !showAssayDetails"
          >
            {{ assayName }}
            <span class="icon is-small ml-1">
              <i
                class="fa fa-xs"
                :class="showAssayDetails ? 'fa-chevron-up' : 'fa-chevron-down'"
              />
            </span>
          </a>
          <div
            v-if="showAssayDetails && previewDv.isMetaReady"
            class="assay-content box p-0 mt-3"
          >
            <AssayView :dv="previewDv" />
          </div>
        </div>
        <div class="verify-section">
          <label class="verify-section-label">
            Collections ({{ collectionIds.length }})
          </label>
          <div class="datasets-per-collections">
            <div
              v-for="[collectionId, dimensionsValues] of collectionIds.map(
                (id) => [id, collections[id].dimensionsValues],
              )"
              :key="`verif-collection-${collectionId}`"
              class="collection-datasets"
            >
              <CollectionOrganiser
                class="single-collection"
                :collection-ids="[collectionId]"
                :can-edit="false"
                :show-count="false"
                :show-dataset-count="true"
                :show-not-assigned-count="false"
                :selected-collection-id="collectionId"
              >
                <template #collection>
                  <Collection
                    class="collection-container"
                    style="position: relative"
                    :collection-id="collectionId"
                  >
                    <template #collection-header>
                      <div class="collection-header-grid">
                        <div class="collection-identity">
                          <h4 class="title is-size-5 mb-1">
                            {{ collections[collectionId].name }}
                          </h4>
                          <div class="is-size-7 has-text-grey">
                            <span v-if="collectionIsRawData[collectionId]">
                              Collection of raw datasets &middot;
                            </span>
                            <span>
                              {{
                                datasetsListsByCollectionId[collectionId]
                                  ?.length || 0
                              }}
                              datasets
                            </span>
                          </div>
                        </div>
                        <div class="form-fields collection-form">
                          <div class="field">
                            <ItemDetailField
                              label="Assign samples"
                              :is-editing="true"
                              help="Assigning a sample to a dataset is crucial to understand the data and e.g. helps building a more complete relationship lineage."
                            >
                              <div
                                class="select is-small"
                                :class="{
                                  'is-danger':
                                    collectionAssignSamples[collectionId] &&
                                    !sampleType,
                                }"
                              >
                                <select
                                  :disabled="
                                    collectionIsRawData[collectionId] &&
                                    sampleTypesOptions.length <= 1
                                  "
                                  :value="
                                    collectionAssignSamples[collectionId]
                                      ? sampleType
                                      : ''
                                  "
                                  @change="
                                    onAssignSampleChange($event, collectionId)
                                  "
                                >
                                  <option
                                    v-if="!collectionIsRawData[collectionId]"
                                    value=""
                                  >
                                    No
                                  </option>
                                  <option
                                    v-for="{ id, label } in sampleTypesOptions"
                                    :key="`opt-${id}`"
                                    :value="id"
                                  >
                                    {{ label }}
                                  </option>
                                </select>
                              </div>
                            </ItemDetailField>
                          </div>
                          <div class="field">
                            <ItemDetailField
                              label="Study"
                              :is-editing="true"
                              help="Datasets will be registered and associated to one or more study. Please select the study for the collection."
                            >
                              <div class="study-field">
                                <SelectContainer
                                  v-if="studyOptions.length > 0"
                                  :key="`study-${collectionId}-${collectionStudyIds[
                                    collectionId
                                  ].join('-')}${studyOptions.length}`"
                                  :value="
                                    collectionStudies[collectionId] || null
                                  "
                                  :options="studyOptions"
                                  placeholder="Choose study"
                                  track-by="_flatId"
                                  label="_displayName"
                                  :multiple="true"
                                  :validation-rules="[
                                    {
                                      name: 'required',
                                      arguments: [],
                                      error_message:
                                        'Please select at least one study for this collection',
                                    },
                                  ]"
                                  @input="(v) => onChange(v, collectionId)"
                                />
                                <div class="study-actions">
                                  <BaseButton
                                    v-tooltip="'Search for studies'"
                                    variant="primary"
                                    :outlined="true"
                                    size="small"
                                    :icon="['fa', 'fa-search']"
                                    label="Load"
                                    @click="
                                      () => {
                                        clickedCollectionId = collectionId
                                        openPicker('search')
                                      }
                                    "
                                  />
                                  <BaseButton
                                    v-tooltip="'Create a new study'"
                                    variant="primary"
                                    :outlined="true"
                                    size="small"
                                    :icon="['fa', 'fa-plus-circle']"
                                    label="Create"
                                    @click="
                                      () => {
                                        clickedCollectionId = collectionId
                                        openPicker('create')
                                      }
                                    "
                                  />
                                </div>
                                <p
                                  v-if="!studyOptions.length"
                                  class="is-size-7 has-text-danger"
                                >
                                  Please
                                  <b>search</b>
                                  for studies or create a
                                  <b>new</b>
                                  one
                                </p>
                              </div>
                            </ItemDetailField>
                          </div>
                        </div>
                      </div>
                    </template>
                    <template #overview-tags />

                    <template #collection-details>
                      <div
                        class="dataloader-table-container"
                        style="
                          position: relative;
                          max-height: 500px;
                          overflow-x: auto;
                          border-bottom: 1px solid var(--color-border-default);
                        "
                      >
                        <table
                          :key="`index${tableIdx}`"
                          class="dataloader verify"
                        >
                          <thead style="position: sticky; top: 0; z-index: 1">
                            <tr>
                              <th class="header-cell index" :rowspan="2"></th>
                              <th
                                class="header-cell bottom-border right-border"
                                rowspan="2"
                                style="width: 150px"
                              >
                                <div>Dataset Name</div>
                              </th>
                              <th class="header-cell" colspan="99">
                                <div>Files</div>
                              </th>
                            </tr>
                            <tr>
                              <th class="header-cell"><div>Type</div></th>
                              <th class="header-cell"><div>Location</div></th>
                              <th class="header-cell" style="width: 100px">
                                <div>Size</div>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <template
                              v-for="(
                                { id, name, files }, datasetIndex
                              ) in datasetsListsByCollectionId[collectionId]"
                              :key="`dataset-${datasetIndex}-${id}`"
                            >
                              <tr
                                v-for="({ uri, size }, fIx) in files"
                                :key="`datset-row${datasetIndex}${fIx}-${id}`"
                              >
                                <td
                                  v-if="fIx === 0"
                                  class="cell index-cell bottom-border"
                                  :rowspan="files.length"
                                >
                                  <div>{{ datasetIndex + 1 }}</div>
                                </td>
                                <td
                                  v-if="fIx === 0"
                                  class="cell bottom-border"
                                  :rowspan="files.length"
                                >
                                  <div
                                    style="
                                      width: 200px;
                                      overflow: hidden;
                                      text-overflow: ellipsis;
                                      vertical-align: baseline;
                                    "
                                  >
                                    <div>{{ name }}</div>
                                    <div
                                      v-if="files.length > 1"
                                      class="help has-text-grey"
                                      style="margin-top: 2px"
                                    >
                                      ({{ files.length }} datafiles)
                                    </div>
                                  </div>
                                </td>
                                <td
                                  class="cell bottom-border"
                                  style="width: 100px"
                                >
                                  <div v-if="dimensionsValues">
                                    <span v-if="dimensionsValues[fIx]">
                                      {{
                                        dimensionsValues[fIx] !== 'na'
                                          ? dimensionsValues[fIx]
                                          : '—'
                                      }}
                                    </span>
                                    <span v-else class="has-text-grey-light">
                                      —
                                    </span>
                                  </div>
                                  <div v-else></div>
                                </td>
                                <td class="cell bottom-border">
                                  <div>{{ uri }}</div>
                                </td>
                                <td class="cell bottom-border">
                                  <div>
                                    {{ (size && prettySize(size || 0)) || '—' }}
                                  </div>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </template>
                  </Collection>
                </template>
              </CollectionOrganiser>
            </div>
          </div>
        </div>
      </div>
      <RecordPickerModal
        v-model="isOpen"
        :initial-tab="initialTab"
        app-name="core"
        model-name="studies"
        @select="selectCollectionStudies"
      />
    </template>
    <template #footer>
      <router-link :to="$attrs.nextRoute">
        <BaseButton
          variant="primary"
          :outlined="!canContinue"
          :disabled="!canContinue"
          :icon="['fa', 'fa-step-forward']"
          label="Continue"
        />
      </router-link>
    </template>
  </StickyFooter>
</template>

<script>
import { computed } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import { useRecordPicker } from '@/composables/useRecordPicker'
import { useRecordPreview } from '@/composables/useRecordPreview'
import { useStore } from '@/store'
import { prettySize } from '@/utils'

import Collection from '@/components/data-loader/Collection.vue'
import CollectionOrganiser from '@/components/data-loader/CollectionOrganiser.vue'
import StickyFooter from '@/components/data-loader/StickyFooter.vue'
import AssayView from '@/components/detail/assay/AssayView.vue'
import ItemDetailField from '@/components/fields/ItemDetailField.vue'
import RecordPickerModal from '@/components/RecordPickerModal.vue'
import SelectContainer from '@/components/SelectContainer.vue'

export default {
  name: 'GenericVerifyView',
  components: {
    StickyFooter,
    AssayView,
    CollectionOrganiser,
    Collection,
    ItemDetailField,
    RecordPickerModal,
    SelectContainer,
  },
  setup() {
    const store = useStore()

    const previewDv = useRecordPreview({
      appName: 'assays',
      modelName: 'assays',
      modelType: computed(
        () => store.getters['dataloader/assay/resolvedAssayType'],
      ),
      record: computed(() => store.state.dataloader.assay.assayData),
    })

    const { isOpen, initialTab, openPicker } = useRecordPicker()

    return {
      previewDv,
      isOpen,
      initialTab,
      openPicker,
    }
  },
  data() {
    return {
      tableIdx: 0,
      clickedCollectionId: '',
      showAssayDetails: false,
    }
  },
  computed: {
    ...mapState('dataloader/assay', {
      assayId: (state) => state.assayData?.id?.value || null,
      storeSampleType: 'sampleType',
    }),
    ...mapState('dataloader/collections', ['collections']),
    ...mapGetters('dataloader/main', ['studyOptions']),
    ...mapGetters('dataloader/assay', {
      assayName: 'assayName',
      sampleTypesOptions: 'getAssaySampleTypeOptions',
    }),
    ...mapGetters('dataloader/datasets', ['datasetsListsByCollectionId']),
    ...mapGetters('dataloader/collections', {
      collectionIds: 'collectionsIdsWithStagedDatasets',
      assignSamples: 'hasAssignSamplesCollection',
    }),
    sampleType: {
      get() {
        return this.storeSampleType
      },
      set(sampleType) {
        this.setSampleType(sampleType)
      },
    },
    missStudies() {
      let missStudies = false
      for (let collectionId of this.collectionIds) {
        if (this.collectionStudies[collectionId] === null) {
          missStudies = true
        }
        break
      }
      return missStudies
    },
    collectionStudies: {
      get() {
        return this.collectionIds.reduce((studies, id) => {
          let collectionStudies = this.collections[id].studies || []
          return {
            ...studies,
            [id]: collectionStudies.length > 0 ? collectionStudies : null,
          }
        }, {})
      },
      set({ collectionId, studies }) {
        this.editCollection({ id: collectionId, editData: { studies } })
      },
    },
    collectionStudyIds() {
      return this.collectionIds.reduce((studyIds, id) => {
        const studies = this.collectionStudies[id] || []
        studyIds[id] = studies.map((s) => s.id)
        return studyIds
      }, {})
    },
    collectionIsRawData() {
      return this.collectionIds.reduce((isRawData, id) => {
        return {
          ...isRawData,
          [id]: this.collections[id].isRawData,
        }
      }, {})
    },
    collectionAssignSamples: {
      get() {
        return this.collectionIds.reduce((assignSamples, id) => {
          const collectionIsRaw = this.collections[id].isRawData
          const collectionAssignSamples = this.collections[id].assignSamples
          return {
            ...assignSamples,
            [id]: collectionIsRaw ? true : collectionAssignSamples,
          }
        }, {})
      },
      set({ collectionId, assignSamples }) {
        this.editCollection({ id: collectionId, editData: { assignSamples } })
      },
    },
    canContinue() {
      return !this.missStudies
    },
  },
  methods: {
    prettySize,
    ...mapMutations('dataloader/main', {
      addStudyOptions: 'ADD_STUDY_OPTIONS',
    }),
    ...mapMutations('dataloader/assay', { setSampleType: 'SET_SAMPLE_TYPE' }),
    ...mapActions('dataloader/collections', ['editCollection']),
    selectCollectionStudies(studies) {
      const beforeOptCount = this.studyOptions.length
      this.addStudyOptions(studies)
      if (beforeOptCount === 0) {
        this.collectionIds.forEach((id) => {
          this.collectionStudies = {
            collectionId: id,
            studies: this.studyOptions,
          }
        })
      }

      this.collectionStudies
    },
    onChange(newOpt, collectionId) {
      this.collectionStudies = {
        collectionId,
        studies: newOpt,
      }
    },
    onAssignSampleChange(event, collectionId) {
      const value = event.target.value
      if (value === '') {
        // "No" selected — disable sample assignment
        this.collectionAssignSamples = {
          collectionId,
          assignSamples: false,
        }
      } else {
        // Sample type selected — enable assignment + set type
        this.collectionAssignSamples = {
          collectionId,
          assignSamples: true,
        }
        this.sampleType = value
      }
    },
  },
}
</script>

<style scoped>
.study-field {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.study-actions {
  display: flex;
  gap: var(--space-1);
}

.verify-body {
  margin: 1rem 0;
}

.verify-section {
  margin-bottom: 1.25rem;
}

.verify-section-label {
  display: block;
  color: var(--gray-700);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin-bottom: 0.35rem;
}

.assay-toggle {
  cursor: pointer;
  text-decoration: none;
}

.assay-content {
  overflow: auto;
  max-height: 500px;
  height: 300px;
  resize: vertical;
  border: 1px solid var(--color-border-default);
}

.assay-content :deep(.panel-block) {
  padding: 0;
}

.assay-content :deep(table tr) {
  border-top: 2px solid var(--color-background-default);
}
.assay-content :deep(table td) {
  padding: 0.2rem 0.5rem;
}

/* Collection header form separator */
.collection-form {
  padding: var(--space-1) !important;

  @include tablet {
    border-left: 2px solid var(--color-border-default);
    padding-left: var(--space-3) !important;
  }
}

/* Hide tab bar when each collection is rendered individually */
.single-collection :deep(.tabs) {
  display: none;
}

.collection-datasets {
  padding-right: var(--space-8);
  margin-bottom: var(--space-4);
}

.collection-header-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-2);
  padding: var(--space-3) 0;

  @include tablet {
    grid-template-columns: 1fr auto;
    gap: var(--space-6);
    align-items: start;
  }
}

.collection-identity {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

table.dataloader.verify {
  border-left: 2px solid gray;
  border-right: 2px solid gray;
}

table.dataloader.verify tr th,
table.dataloader.verify tr td {
  border: 0;
}

table.dataloader.verify thead tr th.header-cell {
  border-top: 1px solid gray;
  border-bottom: 1px solid gray;
}

table.dataloader.verify tr td.cell.bottom-border {
  border-bottom: 7px solid white;
}
table.dataloader.verify tr th.header-cell.right-border {
  border-right: 1px solid gray;
}
</style>
